import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import type React from 'react';
import type { TokenUsageMetrics } from '../../utils/tokenUsageUtils';

interface TokenUsageIndicatorProps {
  metrics: TokenUsageMetrics;
}

const TokenUsageIndicator: React.FC<TokenUsageIndicatorProps> = ({ metrics }) => {
  const theme = useTheme();

  // Format tokens to compact notation, e.g., 850,000 -> 850K
  const formatTokens = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(num);
  };

  const isCritical = metrics.tokenUsagePercentage >= 90;
  const isWarning = metrics.tokenUsagePercentage >= 75 && !isCritical;

  let message = null;
  let icon = null;
  let color = theme.palette.text.secondary;

  if (isCritical) {
    message =
      'Critical: Token usage is nearing the absolute limit. Generative features may fail. Please remove old documents to ensure enough space for tender documents and thinking tokens.';
    icon = <ErrorOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />;
    color = theme.palette.error.main;
  } else if (isWarning) {
    message =
      'Warning: Token usage is high. Consider cleaning up old or unused documents to ensure enough space for tender documents and thinking tokens.';
    icon = <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />;
    color = theme.palette.warning.main;
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          Total Tokens
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
          {formatTokens(metrics.currentLibraryTokens)} / {formatTokens(metrics.tokenLimit)} Tokens (
          {Math.round(metrics.tokenUsagePercentage)}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={metrics.tokenUsagePercentage}
        sx={{
          height: 10,
          borderRadius: 5,
          backgroundColor: theme.palette.greyContrast || theme.palette.grey[200],
          '& .MuiLinearProgress-bar': {
            backgroundColor: isCritical
              ? theme.palette.error.main
              : isWarning
                ? theme.palette.warning.main
                : theme.palette.bidlogiq?.orange,
          },
        }}
      />
      {(isWarning || isCritical) && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          {icon}
          <Typography variant="caption" sx={{ color, fontWeight: 500 }}>
            {message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TokenUsageIndicator;
